import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { X } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";

export default function PartDialog({ open, onClose, part, categories, onSaved }) {
  const { hasRole } = useAuth();
  const isViewer = hasRole("viewer");
  const isEdit = !!part;
  const [form, setForm] = useState({
    sku: "",
    part_number: "",
    name: "",
    category_id: "",
    description: "",
    unit_cost: 0,
    stock_qty: 0,
    low_stock_threshold: 5,
    location: "",
    supplier: "",
    image_url: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (part) {
      const { id, category_name, created_at, updated_at, ...rest } = part;
      setForm(rest);
    } else {
      setForm({
        sku: "",
        part_number: "",
        name: "",
        category_id: categories[0]?.id || "",
        description: "",
        unit_cost: 0,
        stock_qty: 0,
        low_stock_threshold: 5,
        location: "",
        supplier: "",
        image_url: "",
      });
    }
  }, [part, open, categories]);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        unit_cost: Number(form.unit_cost),
        stock_qty: Number(form.stock_qty),
        low_stock_threshold: Number(form.low_stock_threshold),
      };
      if (isViewer) {
        // Viewers don't see cost — never send it (backend will default to 0 on create)
        delete payload.unit_cost;
      }
      if (isEdit) {
        const { stock_qty, ...patch } = payload;
        await api.patch(`/parts/${part.id}`, patch);
        toast.success("Part updated");
      } else {
        await api.post("/parts", payload);
        toast.success("Part created");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#1A2332]/60 flex items-start sm:items-center justify-center pt-4 sm:pt-4 px-0 sm:px-4 pb-0 sm:pb-4 overflow-y-auto" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white border border-zinc-300 w-full max-w-2xl max-h-[92vh] sm:max-h-[90vh] overflow-y-auto"
        data-testid="part-dialog"
      >
        <div className="flex items-center justify-between border-b border-zinc-300 px-4 sm:px-6 py-4 sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF9800]">
              {isEdit ? "Edit record" : "New record"}
            </div>
            <h2 className="font-display text-lg sm:text-xl font-semibold">{isEdit ? "Edit part" : "Add new part"}</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-zinc-100" data-testid="part-dialog-close">
            <X size={18} weight="bold" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="px-4 sm:px-6 py-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="SKU" required>
              <input
                value={form.sku}
                onChange={(e) => set("sku", e.target.value)}
                required
                data-testid="part-sku-input"
                className="input font-mono"
                placeholder="FN-001"
              />
            </Field>
            <Field label="Part Number" required>
              <input
                value={form.part_number}
                onChange={(e) => set("part_number", e.target.value)}
                required
                data-testid="part-number-input"
                className="input font-mono"
                placeholder="MFG-12345"
              />
            </Field>
          </div>

          <Field label="Name" required>
            <input
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              required
              data-testid="part-name-input"
              className="input"
              placeholder="14 in. Steel Fender"
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Category" required>
              <select
                value={form.category_id}
                onChange={(e) => set("category_id", e.target.value)}
                required
                data-testid="part-category-select"
                className="input"
              >
                <option value="">Select…</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Location">
              <input
                value={form.location}
                onChange={(e) => set("location", e.target.value)}
                data-testid="part-location-input"
                className="input font-mono"
                placeholder="Aisle 3 · Shelf B"
              />
            </Field>
          </div>

          <div className={`grid grid-cols-1 ${isViewer ? "sm:grid-cols-2" : "sm:grid-cols-3"} gap-4`}>
            {!isViewer && (
              <Field label="Unit cost ($)">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.unit_cost}
                  onChange={(e) => set("unit_cost", e.target.value)}
                  data-testid="part-unit-cost-input"
                  className="input font-mono"
                />
              </Field>
            )}
            <Field label="Initial stock" disabled={isEdit}>
              <input
                type="number"
                min="0"
                value={form.stock_qty}
                disabled={isEdit}
                onChange={(e) => set("stock_qty", e.target.value)}
                data-testid="part-stock-input"
                className="input font-mono disabled:bg-zinc-100"
              />
            </Field>
            <Field label="Low-stock threshold">
              <input
                type="number"
                min="0"
                value={form.low_stock_threshold}
                onChange={(e) => set("low_stock_threshold", e.target.value)}
                data-testid="part-threshold-input"
                className="input font-mono"
              />
            </Field>
          </div>

          <Field label="Supplier">
            <input
              value={form.supplier}
              onChange={(e) => set("supplier", e.target.value)}
              data-testid="part-supplier-input"
              className="input"
              placeholder="Optional"
            />
          </Field>

          <Field label="Image URL">
            <input
              value={form.image_url}
              onChange={(e) => set("image_url", e.target.value)}
              data-testid="part-image-input"
              className="input"
              placeholder="https://…"
            />
          </Field>

          <Field label="Description">
            <textarea
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              data-testid="part-description-input"
              className="input min-h-[80px]"
              placeholder="Notes, dimensions, fitment…"
            />
          </Field>

          {isEdit && (
            <p className="text-xs text-zinc-500">
              To change stock quantity, use the <span className="font-semibold">Adjust stock</span> action.
            </p>
          )}

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-semibold border border-zinc-300 hover:bg-zinc-100"
              data-testid="part-dialog-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="part-dialog-save"
              className="px-5 py-2 text-sm font-semibold bg-[#FF9800] hover:bg-[#E68900] text-white disabled:opacity-60"
            >
              {saving ? "Saving…" : isEdit ? "Save changes" : "Create part"}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .input {
          width: 100%;
          padding: 10px 12px;
          background: #fff;
          border: 1px solid #d4d4d8;
          font-size: 14px;
          outline: none;
        }
        .input:focus {
          border-color: #1d4ed8;
          box-shadow: 0 0 0 1px #1d4ed8 inset;
        }
      `}</style>
    </div>
  );
}

function Field({ label, required, disabled, children }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-700">
        {label}{required && <span className="text-red-600 ml-1">*</span>}
        {disabled && <span className="text-zinc-400 ml-2 normal-case tracking-normal">(use stock action)</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
